import {
  EndsInCustomTypeFilterEnum,
  EndsInFilterEnum,
  EndsInUnitEnum,
} from "~/types/endsIn";
import type { IEventFilters } from "~/types/eventFilters";

export const endsInFilterKeys = [
  "endsIn",
  "endsInCustomType",
  "endsInValue1",
  "endsInUnit1",
  "endsInValue2",
  "endsInUnit2",
] as const satisfies readonly (keyof IEventFilters)[];

export type EndsInFilters = Pick<
  IEventFilters,
  (typeof endsInFilterKeys)[number]
>;

export type EndsInCustomFields = Omit<EndsInFilters, "endsIn">;

export type EndsInCustomRange =
  | {
      type:
        | EndsInCustomTypeFilterEnum.LESS_THAN
        | EndsInCustomTypeFilterEnum.MORE_THAN;
      value1: number;
      unit1: EndsInUnitEnum;
    }
  | {
      type: EndsInCustomTypeFilterEnum.BETWEEN;
      value1: number;
      unit1: EndsInUnitEnum;
      value2: number;
      unit2: EndsInUnitEnum;
    };

export const labelByEndsInCustomType: Record<
  EndsInCustomTypeFilterEnum,
  string
> = {
  [EndsInCustomTypeFilterEnum.LESS_THAN]: "Less than",
  [EndsInCustomTypeFilterEnum.MORE_THAN]: "More than",
  [EndsInCustomTypeFilterEnum.BETWEEN]: "Between",
};

const unitLabelByEndsInUnit: Record<EndsInUnitEnum, string> = {
  [EndsInUnitEnum.MINUTE]: "Minute",
  [EndsInUnitEnum.HOUR]: "Hour",
  [EndsInUnitEnum.DAY]: "Day",
};

const shortUnitByEndsInUnit: Record<EndsInUnitEnum, string> = {
  [EndsInUnitEnum.MINUTE]: "m",
  [EndsInUnitEnum.HOUR]: "h",
  [EndsInUnitEnum.DAY]: "d",
};

export const pluralEndsInUnitLabel = (unit: EndsInUnitEnum) =>
  `${unitLabelByEndsInUnit[unit]}s`;

export const formatEndsInDuration = (value: number, unit: EndsInUnitEnum) =>
  `${value} ${value === 1 ? unitLabelByEndsInUnit[unit] : pluralEndsInUnitLabel(unit)}`;

const formatShortEndsInDuration = (value: number, unit: EndsInUnitEnum) =>
  `${value} ${shortUnitByEndsInUnit[unit]}`;

export const clearedEndsInCustom: EndsInCustomFields = {
  endsInCustomType: undefined,
  endsInValue1: undefined,
  endsInUnit1: undefined,
  endsInValue2: undefined,
  endsInUnit2: undefined,
};

const isPositiveInteger = (value?: number): value is number =>
  value !== undefined && Number.isInteger(value) && value > 0;

export const parseEndsInCustom = (
  fields: EndsInCustomFields
): EndsInCustomRange | null => {
  const {
    endsInCustomType: type,
    endsInValue1: value1,
    endsInUnit1: unit1,
    endsInValue2: value2,
    endsInUnit2: unit2,
  } = fields;

  if (!type || !isPositiveInteger(value1) || !unit1) {
    return null;
  }

  if (type !== EndsInCustomTypeFilterEnum.BETWEEN) {
    return { type, value1, unit1 };
  }

  if (!isPositiveInteger(value2) || !unit2) {
    return null;
  }

  return { type, value1, unit1, value2, unit2 };
};

const minutesByEndsInUnit: Record<EndsInUnitEnum, number> = {
  [EndsInUnitEnum.MINUTE]: 1,
  [EndsInUnitEnum.HOUR]: 60,
  [EndsInUnitEnum.DAY]: 24 * 60,
};

export const hasEndsInBetweenOrderError = (fields: EndsInCustomFields) => {
  const range = parseEndsInCustom(fields);

  return (
    range?.type === EndsInCustomTypeFilterEnum.BETWEEN &&
    range.value1 * minutesByEndsInUnit[range.unit1] >=
      range.value2 * minutesByEndsInUnit[range.unit2]
  );
};

export const formatEndsInCustomLabel = (
  fields: EndsInCustomFields
): string | null => {
  const range = parseEndsInCustom(fields);

  if (!range) {
    return null;
  }

  const first = formatShortEndsInDuration(range.value1, range.unit1);

  if (range.type === EndsInCustomTypeFilterEnum.BETWEEN) {
    const second = formatShortEndsInDuration(range.value2, range.unit2);

    return range.unit1 === range.unit2
      ? `${range.value1} - ${second}`
      : `${first} - ${second}`;
  }

  return `${range.type === EndsInCustomTypeFilterEnum.LESS_THAN ? "<" : ">"} ${first}`;
};

export const describeEndsInCustom = (
  fields: EndsInCustomFields
): string | null => {
  const range = parseEndsInCustom(fields);

  if (!range) {
    return null;
  }

  const first = formatEndsInDuration(range.value1, range.unit1);

  if (range.type === EndsInCustomTypeFilterEnum.BETWEEN) {
    return `between ${first} and ${formatEndsInDuration(range.value2, range.unit2)}`;
  }

  return `${labelByEndsInCustomType[range.type].toLowerCase()} ${first}`;
};

export const endsInRequestParams = (
  filters: Partial<IEventFilters>
): EndsInFilters => {
  const { endsIn } = filters;

  if (!endsIn) {
    return {};
  }

  if (endsIn !== EndsInFilterEnum.CUSTOM) {
    return { endsIn };
  }

  const range = parseEndsInCustom(filters);

  if (!range) {
    return {};
  }

  const params: EndsInFilters = {
    endsIn,
    endsInCustomType: range.type,
    endsInValue1: range.value1,
    endsInUnit1: range.unit1,
  };

  if (range.type === EndsInCustomTypeFilterEnum.BETWEEN) {
    params.endsInValue2 = range.value2;
    params.endsInUnit2 = range.unit2;
  }

  return params;
};
